import { Injectable, Optional, Inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { RAW_HTTP_ERRORS } from '@marina/api-client';
import { AuthSessionTerminatorService } from '../services/auth-session-terminator.service';
import { StaleSessionError } from '../services/token-refresh-coordinator.service';
import { ApiError } from './api-error';
import {
  HTTP_ERROR_HANDLING_CONFIG,
  type HttpErrorHandlingConfig,
} from './http-error-handling.token';

/**
 * Turns HTTP errors into a message components can show, and an {@link ApiError} they can inspect.
 *
 * Handles:
 * - 400 (Bad Request) - show error message
 * - 403 (Forbidden) - show access denied, redirect to unauthorized page
 * - 404 (Not Found) - show error message
 * - 500 (Server Error) - show error message
 * - Other errors - show generic error message
 *
 * **This interceptor does not end sessions.** It used to: a 401 on any non-auth URL called
 * `logout()` and navigated to the login page. Because interceptors see errors in reverse
 * registration order and this one was registered last, it ran *before* `AuthInterceptor` and
 * decided the outcome before any refresh could be attempted — so a single recoverable 401 logged
 * the user out, and ten parallel ones did it ten times. Deciding whether a 401 means "refresh and
 * retry" or "the session is over" belongs to the auth layer, which is the only place that knows
 * whether a refresh is possible. See `AuthInterceptor`.
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private terminator: AuthSessionTerminatorService,
    @Optional() @Inject(HTTP_ERROR_HANDLING_CONFIG) private httpErrorConfig?: HttpErrorHandlingConfig
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Auth endpoints opt out: their callers classify failures by status and body, and this
    // interceptor's job is to replace exactly those with a message.
    if (request.context.get(RAW_HTTP_ERRORS)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error: unknown) => {
        // Only HTTP failures are ours to describe. Control-flow errors raised inside the auth layer
        // (a request abandoned because its login was replaced, for instance) would otherwise come out
        // the far side as `ApiError: Error Code: undefined`, losing the type the caller needs.
        if (!(error instanceof HttpErrorResponse)) {
          // A request abandoned because its session ended is an outcome, not a fault. Logging it at
          // error level would be noise on the way to the login page.
          if (error instanceof StaleSessionError) {
            this.logAt('info', 'Request skipped — the session has ended', request.url, error);
          }
          return throwError(() => error);
        }

        let errorMessage = 'An unknown error occurred';

        if (error.error instanceof ErrorEvent) {
          errorMessage = `Error: ${error.error.message}`;
        } else {
          switch (error.status) {
            case 400:
              errorMessage =
                (typeof error.error === 'string' ? error.error : null) ||
                error.error?.message ||
                error.error?.error ||
                (error.error?.errors && typeof error.error.errors === 'object'
                  ? Object.values(error.error.errors).flat().join(', ')
                  : null) ||
                'Bad request. Please check your input.';
              break;

            case 401:
              // Reported, never acted on. AuthInterceptor sits inside this one and has already
              // decided what this 401 means by the time it reaches here.
              errorMessage =
                (typeof error.error === 'string' ? error.error : null) ||
                error.error?.message ||
                error.error?.error ||
                'Unauthorized. Please log in again.';
              break;

            case 403:
              errorMessage =
                (typeof error.error === 'string' ? error.error : null) ||
                error.error?.message ||
                error.error?.error ||
                'Access forbidden. You do not have permission to access this resource.';
              if (!this.httpErrorConfig?.suppress403Redirect) {
                this.router.navigate(['/unauthorized']);
              }
              break;

            case 404:
              errorMessage =
                error.error?.message ||
                error.error?.error ||
                'Resource not found.';
              break;

            case 409:
              errorMessage =
                (typeof error.error === 'string' ? error.error : null) ||
                error.error?.message ||
                error.error?.error ||
                'This resource already exists.';
              break;

            case 500:
              errorMessage =
                error.error?.message ||
                error.error?.error ||
                'Internal server error. Please try again later.';
              break;

            case 503:
              errorMessage =
                error.error?.message ||
                error.error?.error ||
                'Service unavailable. Please try again later.';
              break;

            default:
              errorMessage =
                error.error?.message ||
                error.error?.error ||
                `Error Code: ${error.status}`;
          }
        }

        // Severity has to mean something, or it stops being read. `error` is reserved for a defect
        // somebody should investigate; a session ending, a permission being refused or the network
        // dropping are none of those. Every failure used to be logged at error level, so a routine
        // logout painted the console red and a real bug looked exactly like it.
        this.logAt(this.severityOf(error.status), 'HTTP request failed', request.url, {
          message: errorMessage,
          status: error.status,
          reason: error.error?.reason,
          traceId: error.error?.traceId,
          error: error.error,
        });

        // Return structured error so components can access extra fields (e.g. rateLimitResetAt).
        // reason/traceId/sessionExpired are carried through so an auth failure stays diagnosable
        // after the response body has been replaced by a message.
        const apiError = new ApiError(
          errorMessage,
          error.status,
          error.error?.error ?? null,
          error.error?.rateLimitResetAt ?? null,
          error.error?.reason ?? null,
          error.error?.traceId ?? null,
          error.error?.sessionExpired === true,
        );
        return throwError(() => apiError);
      })
    );
  }

  /**
   * How seriously to take a failed request.
   *
   * - **401** — the session ended, or never was. Expected; the auth layer has already acted on it.
   * - **403 / 404 / 409 / 422** — the server answering correctly about this request. Worth seeing,
   *   not worth paging anyone.
   * - **0** — no response at all: offline, DNS, CORS, a cancelled request. A condition, not a defect.
   * - **429** — backpressure, and handled by the retry logic.
   * - **5xx** — something is broken on the server. This is the one that deserves the word "error".
   */
  private severityOf(status: number): 'error' | 'warn' | 'info' {
    if (this.terminator.isSessionOver() || status === 401) {
      return 'info';
    }
    if (status === 0 || status === 429 || (status >= 400 && status < 500)) {
      return 'warn';
    }
    return 'error';
  }

  private logAt(level: 'error' | 'warn' | 'info', message: string, url: string, detail: unknown): void {
    const line = `[HTTP] ${message}: ${url}`;
    if (level === 'error') {
      console.error(line, detail);
    } else if (level === 'warn') {
      console.warn(line, detail);
    } else {
      console.info(line, detail);
    }
  }
}
